import {
  AspectRatio,
  Box,
  Heading,
  HStack,
  Image,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, ReactNode } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import { formatUnits } from "viem";
import { useAccount, useBalance } from "wagmi";

import { Countdown } from "@/components/countdown";
import { HoverCardWithLink } from "@/components/hover-card-with-link";
import { RoundedBadge } from "@/components/rounded-badge";
import { RoundedButton } from "@/components/rounded-button";
import {
  AppRoute,
  MINIMUM_VIABLE_PLUME_TOKEN_AMOUNT,
  PLUME_STAKING_BANNER_ID,
  PLUME_VISION_URL,
  plumeMainnetId,
  RAFFLE_WON_BANNER_ID,
  SECONDS_IN_HOUR,
  STAKING_URL,
  STAND_WITH_CRYPTO_BANNER_ID,
} from "@/constants/shared";
import { useCurrentUserData } from "@/hooks/useCurrentUserData";
import { useDailySpinOnchain } from "@/hooks/useDailySpinOnchain";
import { useHomeBanner } from "@/hooks/useHomeBanner";
import { useRaffle } from "@/hooks/useRaffle";
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from "@/lib/amplitude";
import { isDailySpinDeprecated } from "@/utils";

import xpPathSvg from "/assets/leaderboard/pp.svg";

export const HomepageRightCardSkeleton = () => {
  return <Skeleton h={[286, 286, 286, 235]} borderRadius="2xl" />;
};

type BannerConfigType = {
  title: ReactNode;
  description: ReactNode;
  backgroundImage: string;
  onDismiss?: () => void;
  // TODO: get final hrefs for raffle win and stand w crypto
  href?: string;
  cta?: string | ReactElement;
  slotRight?: ReactElement;
  invertColor?: boolean;
  type: TBannerTypes;
  bgColor?: string;
};
type TBannerTypes =
  | "BridgePlume"
  | "RaffleWon"
  | "ClaimAirdrop"
  | "ClaimBattleWon"
  | "StandWithCrypto"
  | "StakePlume"
  | "Default"
  | "DailySpinEndingSoon";

export const HomepageRightCard = () => {
  const {
    currentUserData,
    address: walletAddress,
    isLoading,
  } = useCurrentUserData();
  const { hasDismissedBannerId, dismissBannerId, isLoadingHomeBannerState } =
    useHomeBanner();
  const { chain } = useAccount();
  const onDismiss = (bannerId: string) => {
    dismissBannerId(bannerId);
  };

  const { seasonOneAllocation } = currentUserData ?? {};

  /* GET PLUME BALANCE */
  const { data } = useBalance({
    address: walletAddress,
  });
  const { value, decimals } = data ?? {};
  const plumeTokenBalance =
    value && decimals ? Number(formatUnits(value, decimals)) : 0;

  /* GET AIRDROP DATA */
  const { unclaimedRaffleWin, isLoadingRaffleData: isLoadingDailySpinData } =
    useRaffle();

  // if they won a battle group, or if they had any allocation, these will not be null
  const { battleGroupWonAt, calculatedTotal, deferred } =
    seasonOneAllocation ?? {};
  // if initial or mainnet were claimed, these will not be null
  const { mainnetClaimTimestamp, initialClaimTimestamp } =
    seasonOneAllocation?.airdropClaim ?? {};
  const bonusEndTimestamp = initialClaimTimestamp
    ? initialClaimTimestamp + 72 * SECONDS_IN_HOUR
    : null;

  // Airdrop banner logic based on mainnet initial claimed (if there is no claim, and there is a calculated total)
  const shouldShowAirdropBanner =
    !initialClaimTimestamp && Boolean(calculatedTotal);

  // Show Banner helper variables
  const shouldShowBridgePlumeBanner =
    (!plumeTokenBalance ||
      plumeTokenBalance < MINIMUM_VIABLE_PLUME_TOKEN_AMOUNT) &&
    chain?.id === plumeMainnetId;
  const shouldShowClaimBattleWonBanner =
    !mainnetClaimTimestamp && battleGroupWonAt;

  const shouldShowRaffleWonBanner =
    unclaimedRaffleWin && !hasDismissedBannerId(RAFFLE_WON_BANNER_ID);

  const shouldShowStandWithCryptoBanner =
    import.meta.env.VITE_SHOW_STAND_WITH_CRYPTO_BANNER === "true" &&
    !hasDismissedBannerId(STAND_WITH_CRYPTO_BANNER_ID);

  const shouldShowStakePlumeBanner =
    initialClaimTimestamp &&
    bonusEndTimestamp &&
    bonusEndTimestamp > new Date().getTime() / 1000 &&
    !hasDismissedBannerId(PLUME_STAKING_BANNER_ID);

  const { hasSpunToday, isLoadingDailySpinStats } = useDailySpinOnchain();

  // Only show Daily Spin Ending Soon banner when feature is active (not deprecated)
  const shouldShowDailySpinEndingSoonBanner =
    !isDailySpinDeprecated() && !hasSpunToday;

  const BannerConfig: Record<TBannerTypes, BannerConfigType> = {
    BridgePlume: {
      title: "Bridge to Plume",
      description:
        "Bridge assets to Plume and receive Plume tokens when choosing gas on destination",
      backgroundImage: "/assets/banner/banner-bridge-bg.avif",
      href: AppRoute.BridgeWithDefaults,
      cta: "Bridge to Plume",
      type: "BridgePlume",
    },
    RaffleWon: {
      title: "Congrats – you won a Raffle",
      description: `${unclaimedRaffleWin?.name} is yours!`,
      backgroundImage: "/assets/banner/banner-raffle-bg.avif",
      cta: "Claim Now",
      href: AppRoute.DailySpin,
      type: "RaffleWon",
    },
    ClaimAirdrop: {
      title: deferred
        ? "Claim 20% of your Base Allocation"
        : "Claim 12% of your PLUME boost",
      description: deferred
        ? "To celebrate the launch of Plume Genesis, claim 20% of your base allocation now - our gift to you for believing in Plume and being with us from the very beginning."
        : "You already chose to claim your base allocation — claim 12% of your PLUME boost now as our gift for joining us on the Plume Genesis journey.",
      backgroundImage: "/assets/banner/banner-airdrop-bg.avif",
      href: AppRoute.SeasonOneClaim,
      cta: "Claim Now",
      type: "ClaimAirdrop",
    },
    ClaimBattleWon: {
      title: "Congrats – you won a claim party",
      description: `You placed in the top of your group, claim your airdrop now!`,
      backgroundImage: "/assets/banner/banner-claim-battle-won-bg.avif",
      href: AppRoute.SeasonOneClaim,
      cta: "Claim Now",
      type: "ClaimBattleWon",
    },
    StandWithCrypto: {
      title: "Stand with Crypto🇺🇸",
      description: (
        <span>
          Stand With Crypto Alliance is a non-profit organization dedicated to
          uniting global crypto advocates.{" "}
          <Box as="span" color="white">
            Join and earn a badge!
          </Box>
        </span>
      ),
      backgroundImage: "/assets/banner/banner-stand-with-crypto-bg.avif",
      onDismiss: () => onDismiss(STAND_WITH_CRYPTO_BANNER_ID),
      cta: "I Stand with Crypto",
      slotRight: (
        <Text fontSize="sm" color="bg" opacity="0.7">
          *Only available to US based users
        </Text>
      ),
      type: "StandWithCrypto",
    },
    StakePlume: {
      title: "Exclusive Plume Staking offer for you!",
      href: STAKING_URL,
      description:
        "Help secure the chain by staking PLUME - do so within 72 hours and earn a 10x PP multiplier, lasting for 90 days up to the amount you just claimed.",
      onDismiss: () => onDismiss(PLUME_STAKING_BANNER_ID),
      backgroundImage: "/assets/banner/banner-stake-plume-bg.avif",
      cta: "Stake PLUME",
      slotRight: (
        <HStack
          color="text.primary"
          fontSize="sm"
          fontFamily="Inter, sans-serif"
          alignItems="center"
        >
          <Image src={xpPathSvg} boxSize={6} />
          <Text color="fg.inverted">10x PP multiplier offer </Text>
          <Countdown
            fontWeight={500}
            color="text.tertiary"
            format="HM"
            date={
              bonusEndTimestamp ? new Date(bonusEndTimestamp * 1000) : undefined
            }
          />
        </HStack>
      ),
      type: "StakePlume",
    },
    Default: {
      title: "RWAs That Feel Just Like Crypto",
      description: (
        <Box maxW="70%">
          Plume is where real-world value stops sitting still. Liquid.
          Swappable. Onchain.
        </Box>
      ),
      invertColor: true,
      href: PLUME_VISION_URL,
      backgroundImage: "/assets/banner/banner-default-bg.avif",
      cta: (
        <>
          Plume&apos;s Vision <FiArrowUpRight />
        </>
      ),
      type: "Default",
    },
    DailySpinEndingSoon: {
      title: "Final Days of Daily Spin",
      description: (
        <>
          Daily spin is ending on Feb 15, 2026. <br />
          Spin now to earn your chance to win!
        </>
      ),
      backgroundImage: "/assets/banner/banner-daily-spin-ending-soon-bg.svg",
      cta: "Spin Now",
      href: AppRoute.DailySpin,
      type: "DailySpinEndingSoon",
      bgColor: "#1C0E39",
    },
  };

  /*
  BANNERS LOGIC:
  - Some banners are dismissable. If dismissed, hide *all* banners until next refresh
  - If banner CTA is complete, show next applicable banner

  ORDER OF PRIORITY OF BANNERS TO SHOW:
  1. Stand with crypto (can be dismissed)
    - Manually enabled by feature flag
    - Displayed as long as the user doesn't have a claim battle win or raffle win banner to display
    - Should be able to enable this just for US users

  2. Claim airdrop gift on day 1
    - Display if user has deferred their season 1 airdrop 
    - Remove only once claimed

  3. Plume Staking
    - Display after the user has claimed airdrop. No other conditions
    - Can be dismissed

  4. Claim battle win
    - Display at any point if user has won claim battle

  5. Bridge to plume
    - Show anytime plume balance = 0 in wallet

  6. Raffle win (can be dismissed)
    - Display if user has > 0 plume in wallet AND has an active raffle prize to claim

  7. Daily spin ending soon
    - Display if daily spin is ending in less than 24 hours

  
  */

  if (
    isLoading ||
    isLoadingHomeBannerState ||
    isLoadingDailySpinData ||
    isLoadingDailySpinStats
  ) {
    return <HomepageRightCardSkeleton />;
  }

  let config: BannerConfigType | undefined;

  if (shouldShowStandWithCryptoBanner) {
    config = BannerConfig.StandWithCrypto;
  } else if (shouldShowAirdropBanner) {
    config = BannerConfig.ClaimAirdrop;
  } else if (shouldShowStakePlumeBanner) {
    config = BannerConfig.StakePlume;
  } else if (shouldShowClaimBattleWonBanner) {
    config = BannerConfig.ClaimBattleWon;
  } else if (shouldShowBridgePlumeBanner) {
    config = BannerConfig.BridgePlume;
  } else if (shouldShowRaffleWonBanner) {
    config = BannerConfig.RaffleWon;
  } else if (shouldShowDailySpinEndingSoonBanner) {
    config = BannerConfig.DailySpinEndingSoon;
  } else {
    config = BannerConfig.Default;
  }
  return (
    <AspectRatio
      w="full"
      ratio={575 / 240}
      h={["286px", "286px", "286px", "286px", "238px"]}
    >
      <HomeBannerCard {...config} />
    </AspectRatio>
  );
};

export const HomeBannerCard = ({
  title,
  description,
  backgroundImage,
  href,
  cta,
  onDismiss,
  slotRight,
  invertColor = false,
  bgColor = "bg",
  type,
}: BannerConfigType) => {
  return (
    <HoverCardWithLink
      href={href}
      cardRootStyles={{ h: "full", w: "full", borderRadius: "2xl" }}
      showHoverIcon={false}
      linkProps={{
        onClick: () =>
          trackAmplitudeEvent(AmplitudeTrackingEvent.PortalClickedBanner, {
            banner_name: type,
          }),
      }}
    >
      <VStack
        p={6}
        gap={4}
        bgImage={`url(${backgroundImage})`}
        backgroundSize="cover"
        backgroundPosition="center"
        bgRepeat="no-repeat"
        alignItems="start"
        h="100%"
        w="100%"
        bgColor={bgColor}
      >
        <Heading
          size="2xl"
          fontWeight="700"
          lineHeight="32px"
          as="div"
          color={invertColor ? "fg" : "fg.inverted"}
        >
          {title}
        </Heading>
        <Heading
          size="lg"
          color={invertColor ? "fg.muted" : "#FFFFFFB2"}
          lineHeight="24px"
          as="div"
          letterSpacing="-0.33px"
          fontWeight="500"
        >
          {description}
        </Heading>
        <HStack mt={2} gap={4}>
          <RoundedBadge
            h={12}
            px={4}
            bgColor="bg"
            _groupHover={{ bgColor: "bg.subtle" }}
          >
            {cta}
          </RoundedBadge>
          {onDismiss && (
            <RoundedButton
              onClick={(e) => {
                e.preventDefault();
                onDismiss();
              }}
              bg="rgba(255, 255, 255, 0.1)"
              color="fg.inverted"
              h={12}
              px={4}
              opacity={0.7}
            >
              Dismiss
            </RoundedButton>
          )}
          {slotRight}
        </HStack>
      </VStack>
    </HoverCardWithLink>
  );
};
